import { useContext } from 'react';

import { RouteContext } from 'src/context';
import { useAppSelector } from 'src/hooks/useStore';
import {
  isInternalHomePath,
  sidebarLandsOnHomePath,
  useFirstAccessibleNavItem,
} from 'src/legacy/components/Sidebar/useFirstAccessibleNavItem';
import { useSidebarItems } from 'src/legacy/components/Sidebar/useSidebarItems';

/**
 * Studio workspaces have no Dashboard. On home paths (`/` and `/portal/*`), show
 * a loading state while SidebarNav redirects to the first accessible sidebar
 * item. Classic workspaces keep the Dashboard as that first item and render
 * HomePageContent on those paths.
 *
 * The decision keys off the sidebar the user actually gets — whether its first
 * accessible destination is a home path — not `isStudioUser`. The two disagree
 * for workspaces put on the studio-sidebar flag without a Studio-onboarded
 * config, and those were the ones flashing the Dashboard before the redirect.
 */
export function useStudioRootRedirect() {
  const { pathname } = useContext(RouteContext);
  const isClient = useAppSelector((state) => state.user.isClient);
  const { sections: mainSidebarSections } = useSidebarItems('main');
  const firstAccessibleNavItem = useFirstAccessibleNavItem(
    mainSidebarSections,
    isClient,
  );

  const isHomePath = isInternalHomePath(pathname);
  const landsOnHome = sidebarLandsOnHomePath(firstAccessibleNavItem);
  const isInternalHome = isHomePath && !isClient;

  return {
    shouldShowHomePage: isInternalHome && landsOnHome,
    isRedirecting: isInternalHome && !landsOnHome,
  };
}
